import { useState, type FormEvent } from 'react'
import s from './Signup.module.css'

const STEPS = ['약관동의', '회원정보입력', '회원가입완료']

type Agree = { key: string; label: string; required: boolean }
const AGREES: Agree[] = [
  { key: 'terms', label: 'AI 오토팀 서비스 이용약관 동의(필수)', required: true },
  { key: 'privacy', label: '개인정보 수집 및 이용 동의(필수)', required: true },
  { key: 'ads', label: '광고성 정보 수신 동의(선택)', required: false },
]

/** 글자수 카운터가 붙는 평범한 텍스트 입력들. 비밀번호·업종·휴대폰은 생김새가 달라 아래에서 따로 그린다. */
type Field = { key: string; label: string; max: number; ph: string; type?: string; required: boolean }
const FIELDS: Field[] = [
  { key: 'userId', label: '아이디', max: 30, ph: 'AI 오토팀에서 사용하실 아이디를 입력하세요.', required: true },
  { key: 'company', label: '회사명', max: 100, ph: '이름을 입력하세요.', required: true },
]
const FIELDS_TAIL: Field[] = [
  { key: 'email', label: '이메일', max: 100, ph: '이메일을 입력하세요.', type: 'email', required: true },
]

const ETC = '직접입력'
const INDUSTRIES = ['요식업', '도소매', '서비스업', '제조업', 'IT/소프트웨어', '교육', ETC]

const REQUIRED_KEYS = ['userId', 'password', 'company', 'industry', 'manager', 'phone', 'email']

function Check() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m7.8 12.2 2.8 2.8 5.6-5.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function Signup() {
  const [step, setStep] = useState(0)
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [form, setForm] = useState<Record<string, string>>({ dial: '+82' })
  const [showPw, setShowPw] = useState(false)

  const allOn = AGREES.every((a) => checked[a.key])
  const canNext = AGREES.filter((a) => a.required).every((a) => checked[a.key])
  const canSubmit =
    REQUIRED_KEYS.every((k) => form[k]?.trim()) &&
    (form.industry !== ETC || !!form.industryEtc?.trim())

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  // ponytail: 목업이라 제출은 콘솔까지만. 가입 API 붙으면 여기서 POST.
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    console.log('signup', { agreements: checked, ...form })
    setStep(2)
  }

  const text = (f: Field) => (
    <label key={f.key} className={s.row}>
      <span className={s.lb}>
        {f.label} {f.required && <em>*</em>}
      </span>
      <span className={s.wrapInput}>
        <input
          type={f.type ?? 'text'}
          maxLength={f.max}
          placeholder={f.ph}
          required={f.required}
          value={form[f.key] ?? ''}
          onChange={(e) => set(f.key, e.target.value)}
        />
        <b className={s.count}>
          {(form[f.key] ?? '').length}/{f.max}
        </b>
      </span>
    </label>
  )

  return (
    <div className={s.page}>
      <main className={s.card}>
        <div className={s.brand}>
          <div className={s.name}>
            <img className={s.mark} src="/assets/logo-mark.png" alt="" />
            AI 오토팀
          </div>
          <p>
            AI 오토팀의 모든 기능을 <b>free 플랜</b>을 사용해보세요.
            <br />
            당신의 업무를 줄여줍니다.
          </p>
        </div>

        <ol className={s.steps}>
          {STEPS.map((label, i) => (
            <li key={label} className={i === step ? s.on : i < step ? s.done : undefined}>
              <span className={s.no}>{i <= step ? <Check /> : i + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <>
            <h1>약관동의</h1>
            <label className={`${s.agree} ${s.all}`}>
              <input
                type="checkbox"
                checked={allOn}
                onChange={(e) =>
                  setChecked(
                    e.target.checked ? Object.fromEntries(AGREES.map((a) => [a.key, true])) : {},
                  )
                }
              />
              전체 동의
            </label>

            {AGREES.map((a) => (
              <label key={a.key} className={s.agree}>
                <input
                  type="checkbox"
                  checked={!!checked[a.key]}
                  onChange={(e) => setChecked((c) => ({ ...c, [a.key]: e.target.checked }))}
                />
                {a.label}
                <a
                  href="#"
                  className={s.view}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  내용 보기 ›
                </a>
              </label>
            ))}

            <button className={s.primary} disabled={!canNext} onClick={() => setStep(1)}>
              다음
            </button>
          </>
        )}

        {step === 1 && (
          <form onSubmit={onSubmit}>
            <h1>회원정보입력</h1>

            {FIELDS.slice(0, 1).map(text)}

            <label className={s.row}>
              <span className={s.lb}>
                비밀번호 <em>*</em>
              </span>
              <span className={`${s.wrapInput} ${s.eye}`}>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="비밀번호를 입력하세요."
                  autoComplete="new-password"
                  minLength={8}
                  required
                  value={form.password ?? ''}
                  onChange={(e) => set('password', e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
                  onClick={() => setShowPw((v) => !v)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="2.8" />
                    {showPw && <path d="m4 20 16-16" />}
                  </svg>
                </button>
              </span>
            </label>

            {FIELDS.slice(1).map(text)}

            <label className={s.row}>
              <span className={s.lb}>
                업종 <em>*</em>
              </span>
              {/* 직접입력이면 같은 자리에서 셀렉트를 입력창으로 바꾼다.
                  되돌아갈 길이 없으면 갇히므로 '목록' 버튼을 같이 둔다. */}
              {form.industry === ETC ? (
                <span className={s.wrapInput}>
                  <input
                    autoFocus
                    maxLength={50}
                    placeholder="업종을 직접 입력하세요."
                    required
                    value={form.industryEtc ?? ''}
                    onChange={(e) => set('industryEtc', e.target.value)}
                  />
                  <b className={s.count}>{(form.industryEtc ?? '').length}/50</b>
                  <button
                    type="button"
                    className={s.back}
                    onClick={() => setForm((f) => ({ ...f, industry: '', industryEtc: '' }))}
                  >
                    목록
                  </button>
                </span>
              ) : (
                <select
                  className={s.select}
                  required
                  value={form.industry ?? ''}
                  onChange={(e) => set('industry', e.target.value)}
                >
                  <option value="">업종을 선택하세요.</option>
                  {INDUSTRIES.map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              )}
            </label>

            <label className={s.row}>
              <span className={s.lb}>
                성명 <em>*</em>
              </span>
              <span className={s.wrapInput}>
                <input
                  maxLength={20}
                  placeholder="성명을 입력하세요."
                  required
                  value={form.manager ?? ''}
                  onChange={(e) => set('manager', e.target.value)}
                />
                <b className={s.count}>{(form.manager ?? '').length}/20</b>
              </span>
            </label>

            <label className={s.row}>
              <span className={s.lb}>
                휴대폰 번호 <em>*</em>
              </span>
              <span className={s.phone}>
                <select
                  className={s.select}
                  value={form.dial}
                  onChange={(e) => set('dial', e.target.value)}
                >
                  <option>+82</option>
                  <option>+1</option>
                  <option>+81</option>
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="'-'를 제외한 숫자만 입력해주세요."
                  required
                  value={form.phone ?? ''}
                  onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
                />
              </span>
            </label>

            {FIELDS_TAIL.map(text)}

            <div className={s.nav}>
              <button type="button" className={s.ghost} onClick={() => setStep(0)}>
                이전
              </button>
              <button type="submit" className={s.primary} disabled={!canSubmit}>
                가입완료
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className={s.done2}>
            <span className={s.big}>
              <Check />
            </span>
            <h1>회원가입완료</h1>
            <p>
              {form.manager}님, 가입이 완료되었습니다.
              <br />
              지금 바로 로그인해 AI 오토팀을 시작해보세요.
            </p>
            <a className={s.primary} href="#/login">
              로그인하러 가기
            </a>
          </div>
        )}
      </main>
    </div>
  )
}
